import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Line, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token';

const C = {
  bg: '#0a0a0b',
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

// Chart colors
const BLUE = '#3B82F6';
const GREEN = '#34D399';
const TEAL = '#2DD4BF';
const RED = '#F87171';

// ---------------- Types (mirror the API contract) ----------------
interface AppleEstimate {
  customer_paid_usd: number;
  commission_usd: number;
  proceeds_usd: number;
}
interface TSPoint { date: string; gross_usd: number; refunds_usd: number; net_usd: number }
interface KeyNet { key: string; net_usd: number }
interface KeyMrr { key: string; mrr_usd: number }
interface ProjMonth { month: string; mrr_usd: number; kind?: string }
interface Projection {
  monthly_churn_rate: number;
  this_month_usd: number;
  next_month_usd: number;
  scheduled_to_cancel_usd: number;
  active_trials?: number;
  assumed_trial_conversion?: number;
  trial_pipeline_usd?: number;
  months: ProjMonth[];
}
interface Summary {
  mrr_usd: number; arr_usd: number; active_subscriptions: number;
  gross_usd: number; net_usd: number; refunds_usd: number; refund_rate: number;
  transactions_count: number; arpu_usd: number; payers: number;
  new_usd: number; renewal_usd: number;
  apple_estimate: AppleEstimate;
  timeseries: TSPoint[];
  by_plan: KeyNet[];
  by_currency: KeyNet[];
  by_country: KeyNet[];
  by_variant: KeyNet[];
  mrr_by_plan: KeyMrr[];
  projection: Projection;
  fx_last_updated: string;
}
interface TxnRow {
  event_at: string; email: string; name: string;
  amount_usd: number; amount: number; currency: string;
  plan: string; event_type: string; paywall_variant: string | null;
  country: string; environment: string;
}
interface TxnResp { total: number; rows: TxnRow[] }

interface UpcomingPayment {
  name: string; email: string; plan: string; kind: string; country: string; bills_at: string;
  amount_usd: number; tax_usd: number; commission_usd: number; proceeds_usd: number;
}
interface RecentPayment {
  name: string; email: string; plan: string; event_type: string;
  amount_usd: number; currency: string; country: string; event_at: string;
  tax_usd: number; commission_usd: number; proceeds_usd: number;
}
interface PaymentTotals { count: number; gross_usd: number; tax_usd: number; commission_usd: number; proceeds_usd: number }
interface UpcomingResp { payments: UpcomingPayment[]; totals: PaymentTotals }
interface PastResp { payments: RecentPayment[]; total: number; totals: PaymentTotals }

const PAST_PAGE = 50;

// "in 3 days" / "tomorrow" / "today", plus the calendar date.
function untilLabel(ts: string): string {
  const d = new Date(ts).getTime();
  const days = Math.round((d - Date.now()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

// ---------------- Formatting helpers ----------------
function fmtUSD(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtNum(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return v.toLocaleString('en-US');
}
function fmtPct(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  // Accept either a fraction (0.12) or a percentage (12); values <=1 treated as fraction.
  const pct = v > 1 ? v : v * 100;
  return pct.toLocaleString('en-US', { maximumFractionDigits: 1 }) + '%';
}
function pctValue(n: number | null | undefined): number {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return v > 1 ? v : v * 100;
}
// short axis label "Jul 1"
function shortDate(d: string): string {
  try {
    const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}
// "Aug" from "2026-08"
function shortMonth(m: string): string {
  try {
    const dt = new Date(m + '-01T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short' });
  } catch { return m; }
}
// relative time for transactions
function relTime(ts: string): string {
  try {
    const then = new Date(ts).getTime();
    const now = Date.now();
    const diff = now - then;
    const min = Math.floor(diff / 60000);
    const hr = Math.floor(diff / 3600000);
    const day = Math.floor(diff / 86400000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    if (hr < 24) return `${hr}h ago`;
    if (day === 1) return 'yesterday';
    if (day < 7) return `${day}d ago`;
    return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ts; }
}
function isGuestEmail(email: string): boolean {
  return !!email && email.endsWith('@device.gardenoai.com');
}
function variantLabel(v: string | null): string {
  if (v === 'a') return 'Paywall A';
  if (v === 'b') return 'Paywall B';
  return '—';
}

// We launched on the App Store in June 2026; the first revenue event is
// 18 June. Anchoring "all time" here rather than at an arbitrary early year
// keeps the charts from opening with months of empty space before launch.
const LAUNCH_DATE = '2026-06-01';

// preset -> {from, to} in YYYY-MM-DD
function computeRange(preset: string): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  if (preset === '7') from.setDate(to.getDate() - 7);
  else if (preset === '30') from.setDate(to.getDate() - 30);
  else if (preset === '90') from.setDate(to.getDate() - 90);
  else if (preset === 'all') return { from: LAUNCH_DATE, to: iso(to) };
  return { from: iso(from), to: iso(to) };
}

// ---------------- Small UI atoms ----------------
const cardStyle: React.CSSProperties = {
  background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20,
};

function KpiCard({ label, value, sub, valueColor }: { label: string; value: string; sub: string; valueColor?: string }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8, lineHeight: 1, color: valueColor || C.text }}>{value}</div>
      <div style={{ fontSize: 13, color: C.sub, marginTop: 8 }}>{sub}</div>
    </div>
  );
}

// horizontal mini-bar row for breakdowns
function MiniBarRow({ label, value, frac, barColor }: { label: string; value: string; frac: number; barColor: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
      <div style={{ width: 90, fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 8, background: C.panelAlt, borderRadius: 100, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(2, Math.min(100, frac * 100))}%`, height: '100%', background: barColor, borderRadius: 100 }} />
      </div>
      <div style={{ width: 78, textAlign: 'right', fontSize: 13, color: C.sub, flexShrink: 0 }}>{value}</div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 10, border: `1px solid ${C.border}`,
  background: C.panelAlt, color: C.text, fontSize: 14, cursor: 'pointer',
};
const btnStyle: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`,
  background: C.panelAlt, color: C.text, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
};

// Dark tooltip for recharts
function DarkTooltip({ active, payload, label, moneyKeys }: any) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
      <div style={{ color: C.sub, marginBottom: 6 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color || C.text, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ textTransform: 'capitalize' }}>{String(p.name).replace(/_usd$/, '')}</span>
          <span style={{ fontWeight: 700 }}>{moneyKeys ? fmtUSD(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

// badge for variant / type
function Badge({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return (
    <span style={{ background: bg, color: fg, padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</span>
  );
}

const TYPE_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  purchase: { bg: '#123524', fg: '#4ADE80', label: 'Purchase' },
  renewal: { bg: '#1e2a4a', fg: '#93C5FD', label: 'Renewal' },
  trial_start: { bg: '#3a2a12', fg: '#F5A623', label: 'Trial' },
  refund: { bg: '#3a1414', fg: '#F87171', label: 'Refund' },
};

// ---------------- Main component ----------------
export type RevenueSubTab = 'overview' | 'upcoming' | 'past';

export default function RevenueTab({ token, isMobile, subTab = 'overview' }: { token: string; isMobile: boolean; subTab?: RevenueSubTab }) {
  // filters
  const [preset, setPreset] = useState('all');
  const [plan, setPlan] = useState('all'); // all | monthly | annual
  const [variant, setVariant] = useState('all'); // all | a | b
  const [excludeAdmins, setExcludeAdmins] = useState(true);

  // data
  const [summary, setSummary] = useState<Summary | null>(null);
  const [txns, setTxns] = useState<TxnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // action state
  const [syncing, setSyncing] = useState(false);
  const [fxRefreshing, setFxRefreshing] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // projection view
  const [projView, setProjView] = useState<'mrr' | 'arr'>('mrr');

  // transactions client-side controls
  const [txnSearch, setTxnSearch] = useState('');
  const [txnPlan, setTxnPlan] = useState('all');
  const [txnType, setTxnType] = useState('all');

  const on401 = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    window.location.reload();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { from, to } = computeRange(preset);
    const q = new URLSearchParams({
      from, to, plan, variant, exclude_admins: String(excludeAdmins),
    }).toString();
    try {
      const [sRes, tRes] = await Promise.all([
        fetch(`${API_BASE}/admin/revenue/summary?${q}`, { headers: authHeaders(token) }),
        fetch(`${API_BASE}/admin/revenue/transactions?limit=100&offset=0&search=&plan=${plan}&variant=${variant}&type=all&exclude_admins=${excludeAdmins}`, { headers: authHeaders(token) }),
      ]);
      if (sRes.status === 401 || sRes.status === 403) { on401(); return; }
      if (!sRes.ok) { setError(`Server error (${sRes.status}).`); return; }
      const sd = await sRes.json();
      setSummary((sd.data ?? sd) as Summary);
      if (tRes.ok) {
        const td = await tRes.json();
        const tr = (td.data ?? td) as TxnResp;
        setTxns(tr.rows ?? []);
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [token, preset, plan, variant, excludeAdmins, on401]);

  useEffect(() => { load(); }, [load]);



  async function runAction(kind: 'backfill' | 'fx-refresh') {
    const setBusy = kind === 'backfill' ? setSyncing : setFxRefreshing;
    setBusy(true);
    setActionMsg(null);
    try {
      const res = await fetch(`${API_BASE}/admin/revenue/${kind}`, {
        method: 'POST', headers: authHeaders(token),
      });
      if (res.status === 401 || res.status === 403) { on401(); return; }
      if (!res.ok) { setActionMsg(`Failed (${res.status}).`); return; }
      const d = await res.json();
      const r = d.data ?? d;
      if (kind === 'backfill') setActionMsg(`${r.inserted ?? 0} imported`);
      else setActionMsg('FX rates updated');
      await load();
    } catch {
      setActionMsg('Could not reach the server.');
    } finally {
      setBusy(false);
    }
  }

  // client-side transaction filtering
  const filteredTxns = useMemo(() => {
    return txns.filter((t) => {
      if (txnPlan !== 'all' && t.plan !== txnPlan) return false;
      if (txnType !== 'all' && t.event_type !== txnType) return false;
      if (txnSearch) {
        const q = txnSearch.toLowerCase();
        if (!`${t.email} ${t.name} ${t.country} ${t.currency}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [txns, txnPlan, txnType, txnSearch]);

  const s = summary;
  const hasData = !!s && (s.gross_usd > 0 || s.transactions_count > 0 || (s.timeseries && s.timeseries.length > 0));

  // ---- Loading / error early states ----
  if (loading && !s) {
    return (
      <div style={{ marginTop: 40, color: C.sub, fontSize: 15 }}>Loading revenue…</div>
    );
  }

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* 1. HEADER ROW */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>Revenue Analytics</h1>
          <p style={{ color: C.sub, marginTop: 6, fontSize: 15, maxWidth: 560 }}>
            Real revenue from Apple, converted to USD: gross, net of refunds, MRR, and ARR.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 0, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', flexShrink: 0 }}>
          {[
            { l: 'MRR', v: fmtUSD(s?.mrr_usd) },
            { l: 'ARR', v: fmtUSD(s?.arr_usd) },
            { l: 'Active Subscriptions', v: fmtNum(s?.active_subscriptions) },
          ].map((it, i) => (
            <div key={it.l} style={{ padding: '14px 20px', borderLeft: i ? `1px solid ${C.border}` : 'none', minWidth: 92 }}>
              <div style={{ fontSize: 11, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>{it.l}</div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6, lineHeight: 1 }}>{it.v}</div>
            </div>
          ))}
        </div>
      </div>

      {error && <p style={{ color: RED, marginTop: 16 }}>{error}</p>}

      {(subTab === 'upcoming' || subTab === 'past') && (
        <PaymentsLedger key={subTab} kind={subTab} token={token} excludeAdmins={excludeAdmins} on401={on401} />
      )}

      {subTab === 'overview' && (<>
      {/* 2. FILTER BAR */}
      <div style={{ marginTop: 22, ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <select value={preset} onChange={(e) => setPreset(e.target.value)} style={selectStyle}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time (since launch)</option>
        </select>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} style={selectStyle}>
          <option value="all">All plans</option>
          <option value="monthly">Monthly</option>
          <option value="annual">Annual</option>
        </select>
        <select value={variant} onChange={(e) => setVariant(e.target.value)} style={selectStyle}>
          <option value="all">All variants</option>
          <option value="a">Paywall A</option>
          <option value="b">Paywall B</option>
        </select>

        {/* Exclude admins pill toggle */}
        <button
          onClick={() => setExcludeAdmins((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', borderRadius: 100,
            border: `1px solid ${excludeAdmins ? C.accent : C.border}`, background: C.panelAlt,
            color: C.text, fontSize: 13, cursor: 'pointer', fontWeight: 600,
          }}
        >
          <span style={{
            width: 30, height: 17, borderRadius: 100, background: excludeAdmins ? C.accent : C.border,
            position: 'relative', transition: 'background .15s', flexShrink: 0,
          }}>
            <span style={{
              position: 'absolute', top: 2, left: excludeAdmins ? 15 : 2, width: 13, height: 13,
              borderRadius: '50%', background: '#fff', transition: 'left .15s',
            }} />
          </span>
          Exclude admins
        </button>

        <div style={{ flex: 1 }} />

        <button onClick={() => runAction('backfill')} disabled={syncing} style={{ ...btnStyle, opacity: syncing ? 0.6 : 1 }}>
          {syncing ? 'Syncing…' : 'Sync Apple history'}
        </button>
        <button onClick={() => runAction('fx-refresh')} disabled={fxRefreshing} style={{ ...btnStyle, opacity: fxRefreshing ? 0.6 : 1 }}>
          {fxRefreshing ? 'Refreshing…' : 'Refresh FX rates'}
        </button>
        {actionMsg && <span style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>{actionMsg}</span>}
      </div>

      {!hasData && !loading ? (
        <div style={{ ...cardStyle, marginTop: 22, textAlign: 'center', padding: '48px 20px', color: C.sub }}>
          No revenue data yet. Try "Sync Apple history" or widen the date range.
        </div>
      ) : (
        <>
          {/* 3. KPI CARDS */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${(s?.refunds_usd ?? 0) > 0 ? 6 : 4}, 1fr)`, gap: 14, marginTop: 22 }}>
            {/* Net only differs from gross once a refund exists, and a
                permanent "$0.00 / 0% of gross" refunds card is noise. Both
                are hidden until a refund actually lands, then reappear on
                their own. */}
            {(s?.refunds_usd ?? 0) > 0 && (
              <KpiCard label="Net revenue" value={fmtUSD(s?.net_usd)} sub="after refunds" />
            )}
            <KpiCard label="Revenue" value={fmtUSD(s?.gross_usd)} sub={`${fmtNum(s?.transactions_count)} transactions`} />
            {(s?.refunds_usd ?? 0) > 0 && (
              <KpiCard label="Refunds" value={fmtUSD(s?.refunds_usd)} sub={`${fmtPct(s?.refund_rate)} of gross`} valueColor={RED} />
            )}
            <KpiCard label="ARPU" value={fmtUSD(s?.arpu_usd)} sub={`net / ${fmtNum(s?.payers)} payers`} />
            <KpiCard label="New" value={fmtUSD(s?.new_usd)} sub="first payments" />
            <KpiCard label="Renewal" value={fmtUSD(s?.renewal_usd)} sub="repeat payments" />
          </div>

          {/* 4. APPLE ESTIMATE STRIP */}
          <div style={{ ...cardStyle, marginTop: 18 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 16 : 40 }}>
              <div>
                <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Apple — customers paid</div>
                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{fmtUSD(s?.apple_estimate.customer_paid_usd)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Apple commission (15%)</div>
                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: RED }}>−{fmtUSD(s?.apple_estimate.commission_usd)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Estimated proceeds</div>
                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: GREEN }}>{fmtUSD(s?.apple_estimate.proceeds_usd)}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.sub, marginTop: 14, lineHeight: 1.5 }}>
              Estimated from logged transactions (Small Business Program, flat 15%). Territory VAT not modelled.
            </div>
          </div>

          {/* 5. REVENUE OVER TIME */}
          <div style={{ ...cardStyle, marginTop: 18 }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>Revenue over time</div>
            <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>Daily revenue in the selected window — gross bars, net line.</div>
            <div style={{ height: 280, marginTop: 16 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={s?.timeseries ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: C.sub, fontSize: 12 }} axisLine={{ stroke: C.border }} tickLine={false} minTickGap={20} />
                  <YAxis tickFormatter={(v) => '$' + v} tick={{ fill: C.sub, fontSize: 12 }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip content={<DarkTooltip moneyKeys labelFormatter={shortDate} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="gross_usd" name="revenue" fill={BLUE} radius={[3, 3, 0, 0]} maxBarSize={26} />
                  {(s?.refunds_usd ?? 0) > 0 && (
                    <Line type="monotone" dataKey="net_usd" name="net" stroke={GREEN} strokeWidth={2} dot={false} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 6. MRR/ARR, plus the refunds breakdown once there is one to show.
              With no refunds the MRR card takes the full width rather than
              sitting beside an empty panel. */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile || (s?.refunds_usd ?? 0) === 0 ? '1fr' : '1fr 1fr', gap: 18, marginTop: 18 }}>
            {/* MRR / ARR card */}
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{fmtUSD(s?.mrr_usd)}</div>
                  <div style={{ fontSize: 13, color: C.sub, marginTop: 8 }}>MRR · {fmtNum(s?.active_subscriptions)} active subscriptions</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{fmtUSD(s?.arr_usd)}</div>
                  <div style={{ fontSize: 12, color: C.sub, marginTop: 6 }}>ARR</div>
                </div>
              </div>
              <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                <div style={{ fontSize: 13, color: C.sub, fontWeight: 600 }}>By plan</div>
                {(() => {
                  const rows = s?.mrr_by_plan ?? [];
                  const max = Math.max(1, ...rows.map((r) => r.mrr_usd));
                  if (!rows.length) return <div style={{ fontSize: 13, color: C.sub, marginTop: 10 }}>No plan data.</div>;
                  return rows.map((r) => (
                    <MiniBarRow key={r.key} label={r.key} value={fmtUSD(r.mrr_usd)} frac={r.mrr_usd / max} barColor={TEAL} />
                  ));
                })()}
              </div>
            </div>

            {/* Refunds card */}
            {(s?.refunds_usd ?? 0) > 0 && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1, color: RED }}>{fmtUSD(s?.refunds_usd)}</div>
                  <div style={{ fontSize: 13, color: C.sub, marginTop: 8 }}>Refunds in window</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 14, color: C.sub, fontWeight: 600 }}>{fmtPct(s?.refund_rate)} refund rate</div>
              </div>
              {(() => {
                const gross = s?.gross_usd ?? 0;
                const refunds = s?.refunds_usd ?? 0;
                const net = s?.net_usd ?? Math.max(0, gross - refunds);
                const total = Math.max(1, gross);
                const netPct = Math.max(0, Math.min(100, (net / total) * 100));
                const refPct = Math.max(0, Math.min(100 - netPct, (refunds / total) * 100));
                return (
                  <>
                    <div style={{ marginTop: 24, height: 12, background: C.panelAlt, borderRadius: 100, overflow: 'hidden', display: 'flex' }}>
                      <div style={{ width: `${netPct}%`, background: GREEN, height: '100%' }} />
                      <div style={{ width: `${refPct}%`, background: RED, height: '100%' }} />
                    </div>
                    <div style={{ fontSize: 13, color: C.sub, marginTop: 12 }}>
                      Net {fmtUSD(net)} · Gross {fmtUSD(gross)} · Refunds {fmtUSD(refunds)}
                    </div>
                  </>
                );
              })()}
            </div>
            )}
          </div>

          {/* 7. PROJECTION CARD */}
          {(() => {
            const proj = s?.projection;
            const mult = projView === 'arr' ? 12 : 1;
            const churn = pctValue(proj?.monthly_churn_rate);
            const thisM = (proj?.this_month_usd ?? 0) * mult;
            const nextM = (proj?.next_month_usd ?? 0) * mult;
            const delta = nextM - thisM;
            const months = proj?.months ?? [];
            const lastActualIdx = months.reduce((acc, m, i) => (m.kind === 'actual' ? i : acc), -1);
            // Bridge point: the last actual month also seeds the projected series so the two lines connect.
            const chartData = months.map((m, i) => ({
              month: m.month,
              actual: m.kind === 'actual' ? m.mrr_usd * mult : null,
              projected: m.kind !== 'actual' || i === lastActualIdx ? m.mrr_usd * mult : null,
            }));
            return (
              <div style={{ ...cardStyle, marginTop: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ maxWidth: 640 }}>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>MRR / ARR projection</div>
                    <div style={{ fontSize: 13, color: C.sub, marginTop: 4, lineHeight: 1.5 }}>
                      Projection of the current book: paid MRR plus the {s?.projection?.active_trials ?? 0} active trials converting on their end dates, weighted by the measured trial survival rate — {Math.round((s?.projection?.assumed_trial_conversion ?? 0) * 100)}% of past trials kept auto-renew on. Cancellations adjust this graph in real time. Churn and new signups not modelled yet (too little data).
                    </div>
                  </div>
                  {/* segmented control */}
                  <div style={{ display: 'flex', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                    {(['mrr', 'arr'] as const).map((v) => (
                      <button key={v} onClick={() => setProjView(v)} style={{
                        padding: '7px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                        background: projView === v ? C.accent : 'transparent',
                        color: projView === v ? '#fff' : C.sub, textTransform: 'uppercase',
                      }}>{v}</button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 14, marginTop: 18 }}>
                  <div>
                    <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>This month</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{fmtUSD(thisM)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Next month</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      {fmtUSD(nextM)}
                      {delta < 0 && <span style={{ fontSize: 13, color: RED, fontWeight: 700 }}>↓ {fmtUSD(Math.abs(delta))}</span>}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Trial survival</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{Math.round((proj?.assumed_trial_conversion ?? 0) * 100)}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Scheduled to cancel</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: RED }}>{fmtUSD(proj?.scheduled_to_cancel_usd)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>Trial pipeline</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: GREEN }}>+{fmtUSD(proj?.trial_pipeline_usd)}</div>
                  </div>
                </div>

                <div style={{ height: 220, marginTop: 18 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="projFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={TEAL} stopOpacity={0.5} />
                          <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="actualFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={BLUE} stopOpacity={0.45} />
                          <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={shortMonth} tick={{ fill: C.sub, fontSize: 12 }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tickFormatter={(v) => '$' + v} tick={{ fill: C.sub, fontSize: 12 }} axisLine={false} tickLine={false} width={54} />
                      <Tooltip content={<DarkTooltip moneyKeys />} cursor={{ stroke: C.border }} />
                      {/* Realised months (solid blue) flow into the projected teal line via the bridge point. */}
                      <Area type="monotone" dataKey="actual" name="actual" stroke={BLUE} strokeWidth={2} fill="url(#actualFill)" connectNulls={false} />
                      <Area type="monotone" dataKey="projected" name="projected" stroke={TEAL} strokeWidth={2} strokeDasharray="6 3" fill="url(#projFill)" connectNulls={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: C.sub }}>
                  <span><span style={{ color: BLUE }}>■</span> actual (realised revenue)</span>
                  <span><span style={{ color: TEAL }}>■</span> projected</span>
                </div>
              </div>
            );
          })()}

          {/* 8. BREAKDOWN CARDS */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 14, marginTop: 18 }}>
            {([
              { title: 'Revenue by plan', rows: s?.by_plan ?? [], mapKey: (k: string) => k, color: GREEN },
              { title: 'Revenue by variant', rows: s?.by_variant ?? [], mapKey: (k: string) => variantLabel(k), color: BLUE },
              { title: 'Revenue by currency', rows: s?.by_currency ?? [], mapKey: (k: string) => k, color: TEAL },
              { title: 'Revenue by country', rows: s?.by_country ?? [], mapKey: (k: string) => k, color: '#A78BFA' },
            ]).map((b) => {
              const sorted = [...b.rows].sort((x, y) => y.net_usd - x.net_usd);
              const max = Math.max(1, ...sorted.map((r) => r.net_usd));
              return (
                <div key={b.title} style={cardStyle}>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{b.title}</div>
                  {sorted.length === 0 ? (
                    <div style={{ fontSize: 13, color: C.sub, marginTop: 12 }}>No data.</div>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      {sorted.map((r) => (
                        <MiniBarRow key={r.key || '—'} label={b.mapKey(r.key)} value={fmtUSD(r.net_usd)} frac={r.net_usd / max} barColor={b.color} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 9. TRANSACTIONS */}
          <div style={{ marginTop: 26 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Transactions</div>
            <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>Latest revenue events (USD-converted).</div>

            <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={txnSearch} onChange={(e) => setTxnSearch(e.target.value)} placeholder="Search email, name, country…"
                style={{ flex: 1, minWidth: 220, padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panel, color: C.text, fontSize: 14 }} />
              <select value={txnPlan} onChange={(e) => setTxnPlan(e.target.value)} style={selectStyle}>
                <option value="all">All plans</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
              <select value={txnType} onChange={(e) => setTxnType(e.target.value)} style={selectStyle}>
                <option value="all">All types</option>
                <option value="purchase">Purchase</option>
                <option value="renewal">Renewal</option>
                <option value="trial_start">Trial</option>
                <option value="refund">Refund</option>
              </select>
            </div>

            <div style={{ marginTop: 14, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: C.sub }}>
                      {['Variant', 'When', 'Email', 'Amount', 'Plan', 'Type', 'Country'].map((h) => (
                        <th key={h} style={{ padding: '12px 10px', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTxns.map((t, i) => {
                      const tb = TYPE_BADGE[t.event_type] || { bg: '#1c1c1c', fg: C.sub, label: t.event_type };
                      const guest = isGuestEmail(t.email);
                      return (
                        <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                          <td style={{ padding: '12px 10px' }}>
                            {t.paywall_variant === 'a' ? <Badge text="A" bg="#1e2a4a" fg="#93C5FD" />
                              : t.paywall_variant === 'b' ? <Badge text="B" bg="#241f3d" fg="#A78BFA" />
                              : <span style={{ color: C.sub }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 10px', color: C.sub, whiteSpace: 'nowrap' }}>{relTime(t.event_at)}</td>
                          <td style={{ padding: '12px 10px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {guest ? <span style={{ color: C.sub }}>Guest device</span> : (t.email || <span style={{ color: C.sub }}>—</span>)}
                          </td>
                          <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 700 }}>{fmtUSD(t.amount_usd)}</span>
                            {t.currency && t.currency !== 'USD' && (
                              <span style={{ color: C.sub, fontSize: 12, marginLeft: 6 }}>({t.currency} {t.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 10px', textTransform: 'capitalize' }}>{t.plan || '—'}</td>
                          <td style={{ padding: '12px 10px' }}><Badge text={tb.label} bg={tb.bg} fg={tb.fg} /></td>
                          <td style={{ padding: '12px 10px', color: t.country ? C.text : C.sub, whiteSpace: 'nowrap' }}>{t.country || '—'}</td>
                        </tr>
                      );
                    })}
                    {filteredTxns.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>No transactions to show.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

// ---------------- Payments ledger (Revenue → Upcoming / Past) ----------------

type LedgerPeriod = '30d' | 'month' | 'all';

const thL: React.CSSProperties = { padding: '12px 10px', fontWeight: 600 };
const tdL: React.CSSProperties = { padding: '12px 10px', whiteSpace: 'nowrap' };

function userCell(name: string, email: string) {
  return name || (isGuestEmail(email) ? <span style={{ color: C.sub }}>Guest device</span> : (email || <span style={{ color: C.sub }}>—</span>));
}

// Customer paid / Tax / Apple 15% / You get, shared by rows and the totals line.
function moneyCells(gross: number, tax: number, commission: number, proceeds: number, bold = false) {
  return (
    <>
      <td style={{ ...tdL, fontWeight: bold ? 800 : 700 }}>{fmtUSD(gross)}</td>
      <td style={{ ...tdL, color: tax > 0 ? '#F87171' : C.sub, fontWeight: bold ? 700 : 400 }}>{tax > 0 ? `-${fmtUSD(tax)}` : '—'}</td>
      <td style={{ ...tdL, color: '#F87171', fontWeight: bold ? 700 : 400 }}>-{fmtUSD(commission)}</td>
      <td style={{ ...tdL, color: '#4ADE80', fontWeight: bold ? 800 : 700 }}>{fmtUSD(proceeds)}</td>
    </>
  );
}

function PeriodToggle({ value, onChange, kind }: { value: LedgerPeriod; onChange: (p: LedgerPeriod) => void; kind: 'upcoming' | 'past' }) {
  const options: [LedgerPeriod, string][] = [
    ['30d', kind === 'upcoming' ? 'Next 30 days' : 'Last 30 days'],
    ['month', 'This month'],
    ['all', 'All'],
  ];
  return (
    <div style={{ display: 'inline-flex', flexWrap: 'wrap', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 3 }}>
      {options.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
          style={{ border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: value === k ? C.accent : 'transparent', color: value === k ? '#fff' : C.sub }}>
          {label}
        </button>
      ))}
    </div>
  );
}

// The ledger's sum, under the table. Covers every payment the filter matches,
// not just the page on screen.
function TotalsCard({ totals, label, note }: { totals: PaymentTotals | null; label: string; note: string }) {
  const items = [
    { l: 'Payments', v: fmtNum(totals?.count), c: C.text },
    { l: 'Customers pay', v: fmtUSD(totals?.gross_usd), c: C.text },
    { l: 'Tax', v: `-${fmtUSD(totals?.tax_usd)}`, c: '#F87171' },
    { l: 'Apple 15%', v: `-${fmtUSD(totals?.commission_usd)}`, c: '#F87171' },
    { l: 'You get', v: fmtUSD(totals?.proceeds_usd), c: '#4ADE80' },
  ];
  return (
    <div style={{ marginTop: 16, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px' }}>
      <div style={{ fontSize: 15, fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3 }}>{note}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
        {items.map((it) => (
          <div key={it.l} style={{ flex: '1 1 130px', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.4 }}>{it.l}</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6, color: it.c }}>{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsLedger({ kind, token, excludeAdmins, on401 }: { kind: 'upcoming' | 'past'; token: string; excludeAdmins: boolean; on401: () => void }) {
  const [period, setPeriod] = useState<LedgerPeriod>('30d');
  const [page, setPage] = useState(0);
  const [upcoming, setUpcoming] = useState<UpcomingResp | null>(null);
  const [past, setPast] = useState<PastResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A different filter is a different list; start it from the top.
  useEffect(() => { setPage(0); }, [period, excludeAdmins]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const url = kind === 'upcoming'
          ? `${API_BASE}/admin/revenue/upcoming?period=${period}&exclude_admins=${excludeAdmins}`
          : `${API_BASE}/admin/revenue/past?period=${period}&exclude_admins=${excludeAdmins}&limit=${PAST_PAGE}&offset=${page * PAST_PAGE}`;
        const res = await fetch(url, { headers: authHeaders(token) });
        if (res.status === 401 || res.status === 403) { on401(); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (cancelled) return;
        if (kind === 'upcoming') setUpcoming((d.data ?? d) as UpcomingResp);
        else setPast((d.data ?? d) as PastResp);
      } catch (e: any) {
        if (!cancelled) setErr(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, period, page, excludeAdmins, token, on401]);

  const monthName = new Date().toLocaleDateString('en-US', { month: 'long', timeZone: 'Asia/Karachi' });
  const scope = period === 'month' ? monthName : period === '30d' ? (kind === 'upcoming' ? 'next 30 days' : 'last 30 days') : 'all time';

  const header = (title: string, sub: string) => (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.sub, marginTop: 4, maxWidth: 640 }}>{sub}</div>
      </div>
      <PeriodToggle value={period} onChange={setPeriod} kind={kind} />
    </div>
  );
  const emptyRow = (cols: number, text: string) => (
    <tr><td colSpan={cols} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>{text}</td></tr>
  );

  if (kind === 'upcoming') {
    const rows = upcoming?.payments ?? [];
    return (
      <div style={{ marginTop: 22 }}>
        {header('Upcoming payments',
          period === 'month'
            ? `Trials converting and subscriptions renewing before the end of ${monthName}. A user can still cancel before the charge.`
            : period === '30d'
              ? 'Trials converting and subscriptions renewing in the next 30 days. A user can still cancel before the charge.'
              : 'Every trial and subscription still due to bill, soonest first. A user can still cancel before the charge.')}
        {err && <p style={{ color: '#F87171', marginTop: 12 }}>{err}</p>}
        <div style={{ marginTop: 14, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: C.sub }}>
                  {['Bills', 'When', 'User', 'Type', 'Plan', 'Country', 'Customer pays', 'Tax', 'Apple 15%', 'You get'].map((h) => <th key={h} style={thL}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={tdL}>{shortDate(p.bills_at)}</td>
                    <td style={{ ...tdL, color: C.sub }}>{untilLabel(p.bills_at)}</td>
                    <td style={{ ...tdL, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{userCell(p.name, p.email)}</td>
                    <td style={tdL}><Badge text={p.kind} bg={p.kind === 'First charge' ? '#241f3d' : '#12261a'} fg={p.kind === 'First charge' ? '#A78BFA' : C.accent} /></td>
                    <td style={{ ...tdL, textTransform: 'capitalize' }}>{p.plan}</td>
                    <td style={{ ...tdL, color: p.country ? C.text : C.sub }}>{p.country || '—'}</td>
                    {moneyCells(p.amount_usd, p.tax_usd, p.commission_usd, p.proceeds_usd)}
                  </tr>
                ))}
                {!loading && rows.length === 0 && emptyRow(10, period === 'month' ? `Nothing else bills in ${monthName}.` : period === '30d' ? 'Nothing bills in the next 30 days.' : 'No upcoming payments.')}
                {loading && rows.length === 0 && emptyRow(10, 'Loading…')}
              </tbody>
            </table>
          </div>
        </div>
        <TotalsCard totals={upcoming?.totals ?? null} label={`Expected, ${scope}`}
          note="If every one of these bills. Trials often cancel first, so treat this as the most it could be. Amounts use what each subscription last paid, or the latest price for that plan in the same country." />
      </div>
    );
  }

  const rows = past?.payments ?? [];
  const total = past?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / PAST_PAGE));
  const navBtn = (disabled: boolean): React.CSSProperties => ({
    background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', color: C.text,
    fontSize: 13, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
  });
  return (
    <div style={{ marginTop: 22 }}>
      {header('Past payments',
        'Charges that actually happened, USD-converted. Apple removes local tax before taking its 15% commission, so "You get" is what actually reaches your bank.')}
      {err && <p style={{ color: '#F87171', marginTop: 12 }}>{err}</p>}
      <div style={{ marginTop: 14, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.sub }}>
                {['When', 'User', 'Customer paid', 'Tax', 'Apple 15%', 'You get', 'Plan', 'Type', 'Country'].map((h) => <th key={h} style={thL}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const tb = TYPE_BADGE[p.event_type] || { bg: '#1c1c1c', fg: C.sub, label: p.event_type };
                return (
                  <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ ...tdL, color: C.sub }}>{relTime(p.event_at)}</td>
                    <td style={{ ...tdL, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{userCell(p.name, p.email)}</td>
                    <td style={tdL}>
                      <span style={{ fontWeight: 700 }}>{fmtUSD(p.amount_usd)}</span>
                      {p.currency && p.currency !== 'USD' && <span style={{ color: C.sub, fontSize: 12, marginLeft: 6 }}>({p.currency})</span>}
                    </td>
                    <td style={{ ...tdL, color: p.tax_usd > 0 ? '#F87171' : C.sub }}>{p.tax_usd > 0 ? `-${fmtUSD(p.tax_usd)}` : '—'}</td>
                    <td style={{ ...tdL, color: '#F87171' }}>-{fmtUSD(p.commission_usd)}</td>
                    <td style={{ ...tdL, color: '#4ADE80', fontWeight: 700 }}>{fmtUSD(p.proceeds_usd)}</td>
                    <td style={{ ...tdL, textTransform: 'capitalize' }}>{p.plan || '—'}</td>
                    <td style={tdL}><Badge text={tb.label} bg={tb.bg} fg={tb.fg} /></td>
                    <td style={{ ...tdL, color: p.country ? C.text : C.sub }}>{p.country || '—'}</td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && emptyRow(9, period === 'month' ? `No payments yet in ${monthName}.` : period === '30d' ? 'No payments in the last 30 days.' : 'No payments yet.')}
              {loading && rows.length === 0 && emptyRow(9, 'Loading…')}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderTop: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: C.sub }}>
              {page * PAST_PAGE + 1}–{Math.min((page + 1) * PAST_PAGE, total)} of {total}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading} style={navBtn(page === 0)}>← Prev</button>
              <span style={{ fontSize: 13, color: C.sub }}>Page {page + 1} of {pages}</span>
              <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1 || loading} style={navBtn(page >= pages - 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>
      <TotalsCard totals={past?.totals ?? null} label={`Total, ${scope}`}
        note={pages > 1 ? `All ${total} payments, not just this page.` : 'Every payment listed above.'} />
    </div>
  );
}
